export type CsvRecord = Record<string, string>;

const stripBom = (value: string): string => value.replace(/^\uFEFF/, '');

export const normalizeHeader = (value: string): string =>
  stripBom(value).trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

export const toCsv = (headers: string[], rows: unknown[][]): string => {
  const escapeCell = (value: unknown) => {
    const text = formatCell(value);
    const needsQuotes = /[",\r\n]/.test(text);
    const escaped = text.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  return [headers, ...rows].map(row => row.map(escapeCell).join(',')).join('\r\n');
};

export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell);
  rows.push(row);

  return rows.filter(currentRow => currentRow.some(value => value.trim() !== ''));
};

export const parseCsvRecords = (text: string): { headers: string[]; records: CsvRecord[] } => {
  const rows = parseCsv(text);
  if (rows.length === 0) {
    return { headers: [], records: [] };
  }

  const headerRowIndex = rows.findIndex(row => row.filter(value => value.trim() !== '').length > 1);
  const headerRow = rows[headerRowIndex >= 0 ? headerRowIndex : 0];
  const headers = headerRow.map(header => stripBom(header).trim());
  const normalizedHeaders = headers.map(normalizeHeader);
  const records = rows.slice((headerRowIndex >= 0 ? headerRowIndex : 0) + 1).map(row => {
    const record: CsvRecord = {};

    headers.forEach((header, index) => {
      const value = (row[index] || '').trim();
      record[header] = value;
      record[normalizedHeaders[index]] = value;
    });

    return record;
  });

  return { headers, records };
};

export const getCsvCell = (record: CsvRecord, aliases: string[]): string => {
  for (const alias of aliases) {
    const directValue = record[alias];
    if (directValue !== undefined && directValue.trim() !== '') {
      return directValue.trim();
    }

    const normalizedValue = record[normalizeHeader(alias)];
    if (normalizedValue !== undefined && normalizedValue.trim() !== '') {
      return normalizedValue.trim();
    }
  }

  return '';
};

export const parseNumber = (value: string, fallback = 0): number => {
  const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
  if (!cleaned) return fallback;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const parseDate = (value: string, fallback = new Date()): Date => {
  if (!value.trim()) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

export const makeImportId = (prefix: string, rowIndex: number): string =>
  `${prefix}-${Date.now()}-${rowIndex}-${Math.random().toString(36).slice(2, 8)}`;

export const downloadTextFile = (filename: string, content: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
